import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { buttonClass } from "@/components/base/buttonStyles";
import ToastLayer from "@/components/base/ToastLayer";
import { useNotifications } from "@/api/notification";

/**
 * 기사 모바일 웹 레이아웃 (요구사항 §8, §9-5).
 * 앱 설치 없이 모바일 브라우저에서 쓰므로 max-w-md 프레임 + 하단 5탭 구조로 고정한다.
 * `알림` 탭은 2026-10-26 에 더했다 — 안 읽은 알림 수를 뱃지로 단다.
 * 아이콘을 쓰지 않으므로 탭은 텍스트 라벨만 둔다.
 */

const TABS = [
  { label: "홈", path: "/driver/home" },
  { label: "송장", path: "/driver/invoice", prefix: "/driver/invoice" },
  { label: "실적", path: "/driver/performance" },
  { label: "알림", path: "/driver/notifications" },
  { label: "내 정보", path: "/driver/profile" },
] as const;

export default function DriverLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { displayName, logout } = useAuth();
  // `알림` 탭의 뱃지. 그 탭을 열면 보인 알림이 읽음이 되어 사라진다(notifications/page.tsx)
  const unread = useNotifications("driver", { is_read: false, size: 1 }).data?.unread ?? 0;

  const isActive = (path: string, prefix?: string) =>
    location.pathname === path ||
    (prefix ? location.pathname.startsWith(`${prefix}/`) : false);

  const handleLogout = async () => {
    await logout();
    navigate("/driver/login", { replace: true });
  };

  return (
    // `h-screen`(100vh) 은 모바일 브라우저에서 주소창·하단 툴바 **뒤까지** 포함한 높이라
    // 하단 탭이 화면 밖으로 밀려 스크롤해야 보였다. `h-dvh` 는 지금 실제로 보이는 높이를
    // 따라간다(툴바가 접혔다 펴져도). 지원하지 않는 브라우저는 `h-screen` 그대로다(`h-dvh` 를 나란히 적으면 CSS 순서상 `h-screen` 이 이긴다)
    <div className="mx-auto flex h-screen supports-[height:100dvh]:h-dvh max-w-md flex-col bg-white">
      <header className="flex shrink-0 items-center justify-between border-b border-gray-100 px-5 pb-3 pt-5">
        <h1 className="text-sm font-semibold tracking-tight text-gray-900">
          한중 상하차 관리
        </h1>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">{displayName}</span>
          <button
            type="button"
            onClick={handleLogout}
            className={buttonClass("secondary", "small")}
          >
            로그아웃
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto">
        <div className="px-5 py-4">
          <Outlet />
        </div>
      </main>

      {/* 홈 인디케이터가 있는 iPhone 에서 탭이 그 아래로 깔리지 않게 안전 영역만큼 띄운다 */}
      <nav className="shrink-0 border-t border-gray-100 pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-stretch">
          {TABS.map((tab) => {
            const active = isActive(tab.path, "prefix" in tab ? tab.prefix : undefined);
            return (
              <Link
                key={tab.path}
                to={tab.path}
                // 하단 탭은 배경으로 표시한다 — 테두리를 쓰면 nav 의 헤어라인과 겹쳐 두 줄이 된다
                className={`flex-1 py-3 text-center text-xs transition-colors ${
                  active
                    ? "bg-primary-50 font-medium text-primary-700"
                    : "text-gray-400"
                }`}
              >
                {tab.label}
                {tab.path === "/driver/notifications" && unread > 0 && (
                  // 아이콘을 쓰지 않으므로(§UI 1) 뱃지는 숫자 하나다 — 기사가 요청한 단 하나의 표시
                  <span className="ml-1 inline-block min-w-[1.125rem] rounded-full bg-amber-500 px-1 text-[11px] font-medium leading-[1.125rem] text-white">
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>
      <ToastLayer />
    </div>
  );
}
